import { useContext, useState } from 'react'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { EmailFormContext } from '../../../EmailForm.context'
import { EmailSelectTemplateToTestVariablesModal } from '../../EmailSelectTemplateToTestVariablesModal'

import {
  getTestVariablesTemplateReports,
  hasFailedTestVariablesReports,
  isGoogleTemplateTestSelected,
  isTextTemplateSelected,
} from './useTestVariables.utils'
import { useTestVariablesAutoCheck } from './useTestVariablesAutoCheck'
import { useTestVariablesReports } from './useTestVariablesReports'
import { useTestVariablesResult } from './useTestVariablesResult'

export function useTestVariables({
  docTemplateCheckRef,
  docTemplates,
  mass,
  textTemplate,
  onDeleteTemplate,
  onEditTemplate,
}) {
  const formContext = useContext(EmailFormContext)

  const [isTesting, setTesting] = useState(false)

  const { getGoogleTemplatesReports, getTextTemplateReport } =
    useTestVariablesReports()

  const { handleTestFail, handleTestResult } = useTestVariablesResult({
    onDeleteTemplate,
    onEditTemplate,
  })

  async function handleTestGoogleVariables({ isAutoCheck, templates }) {
    setTesting(true)

    return getGoogleTemplatesReports(templates)
      .then(reports => {
        const isFailed = hasFailedTestVariablesReports(reports)

        if (isFailed) {
          handleTestFail({ isAutoCheck })
        }

        return handleTestResult({
          templateReports: getTestVariablesTemplateReports({
            docTemplates: templates,
            reports,
          }),
          withConfirmation: !isAutoCheck && !isFailed,
        })
      })
      .catch(() => handleTestFail({ isAutoCheck }))
      .finally(() => setTesting(false))
  }

  async function handleTestEmailTextTemplateVariables({
    isAutoCheck,
  } = EMPTY_OBJECT) {
    setTesting(true)

    return getTextTemplateReport(textTemplate)
      .then(report =>
        handleTestResult({
          templateReports: [{ report }],
          withConfirmation: !isAutoCheck,
        })
      )
      .catch(() => handleTestFail({ isAutoCheck }))
      .finally(() => setTesting(false))
  }

  function handleSelectTemplate(value) {
    if (isGoogleTemplateTestSelected(value)) {
      handleTestGoogleVariables({ templates: docTemplates })
    } else {
      handleTestEmailTextTemplateVariables()
    }
  }

  function handleTestVariables() {
    const hasDocTemplates = docTemplates.length > 0
    const hasTextTemplate = isTextTemplateSelected(textTemplate)

    if (hasDocTemplates && hasTextTemplate) {
      formContext.setModal(
        <EmailSelectTemplateToTestVariablesModal
          onClose={formContext.onCloseModal}
          onSubmit={handleSelectTemplate}
        />
      )
    } else if (hasDocTemplates) {
      handleTestGoogleVariables({ templates: docTemplates })
    } else if (hasTextTemplate) {
      handleTestEmailTextTemplateVariables()
    }
  }

  useTestVariablesAutoCheck({
    docTemplateCheckRef,
    docTemplates,
    mass,
    textTemplate,
    onTestEmailTextTemplateVariables: handleTestEmailTextTemplateVariables,
    onTestGoogleVariables: handleTestGoogleVariables,
  })

  return {
    handleTestVariables,
    isTesting,
  }
}
